import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import 'katex/dist/katex.min.css'
import { markdownHighlights, type TextHighlight } from './markdownHighlights'

/** Links to files or other sites open in a new tab; in-app links stay in the tab. */
function Anchor({ href, children }: { href?: string; children?: React.ReactNode }) {
  const external = !!href && (/^https?:/.test(href) || /\.pdf$/i.test(href))
  return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>{children}</a>
}

const FENCE = /^ {0,3}(`{3,}|~{3,})(.*)$/
const ONE_LINE_MATH = /^ {0,3}\$\$(.*)\$\$[ \t]*$/

/** Rewrite a line that is nothing but `$$…$$` into the fenced form remark-math reads as display math.
 *
 * remark-math only treats `$$` as a block when it opens and closes its own lines, so an agent that
 * writes a whole equation on one line gets cramped inline maths. Fenced code is left exactly as it
 * is, and so are indented code lines, inline `$…$` and `$$` that already spans lines. Escaped `\$`
 * is not special-cased: it already renders literally.
 */
export function displayMath(source: string): string {
  let fence = ''
  return source.split('\n').map(line => {
    const marker = FENCE.exec(line)
    if (marker && !fence) { fence = marker[1]; return line }
    if (marker && fence && marker[1][0] === fence[0] && marker[1].length >= fence.length && !marker[2].trim()) { fence = ''; return line }
    if (fence || /^(\t| {4,})/.test(line)) return line
    const math = ONE_LINE_MATH.exec(line)
    const body = math?.[1].trim()
    return body && !body.includes('$$') ? `\n$$\n${body}\n$$\n` : line
  }).join('\n')
}

export function Markdown({ source, documentLinks, highlights = [] }: { source: string; highlights?: TextHighlight[]; documentLinks?: Record<string, string> }) {
  // Highlight ranges are offsets into the caller's own text, so normalising it would misplace them.
  const text = highlights.length ? source : displayMath(source)
  return <div className="md"><ReactMarkdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkGfm, remarkMath, [markdownHighlights, { source: text, highlights }]]} components={{ a: ({ href, children }) => <Anchor href={href ? documentLinks?.[href] ?? href : href}>{children}</Anchor>,
    // Agent tables can be far wider than the pane they render in, so they scroll instead of cramping.
    table: ({ children }) => <div className="table-wrap"><table>{children}</table></div> }}>{text}</ReactMarkdown></div>
}
